import React, { useState } from 'react';
import {
  Container,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebaseconf';
import { doc, setDoc } from 'firebase/firestore';
import { useInstitution } from '../context/InstitutionContext';
import { useAuth } from '../context/AuthContext';
import { createUserRoleDocument } from '../utils/authUtils';

const steps = ['Institution Details', 'Admin Account', 'Confirm'];

export default function InstitutionRegistration() {
  const navigate = useNavigate();
  const { createInstitution } = useInstitution();
  const { setUserInfo } = useAuth();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [institutionData, setInstitutionData] = useState({
    name: '',
    type: 'college',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    zipCode: '',
    description: '',
    established_year: new Date().getFullYear() - 1,
    board: 'CBSE'
  });

  const [adminData, setAdminData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: ''
  });

  const handleInstitutionChange = (e) => {
    const { name, value } = e.target;
    setInstitutionData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAdminChange = (e) => {
    const { name, value } = e.target;
    setAdminData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateInstitutionStep = () => {
    if (!institutionData.name.trim()) {
      setError('Institution name is required');
      return false;
    }
    if (!institutionData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Valid email is required');
      return false;
    }
    if (!institutionData.phone.match(/^\+?[\d\s\-()]{10,}$/)) {
      setError('Valid phone number is required');
      return false;
    }
    if (!institutionData.address.trim()) {
      setError('Address is required');
      return false;
    }
    setError('');
    return true;
  };

  const validateAdminStep = () => {
    if (!adminData.first_name.trim()) {
      setError('First name is required');
      return false;
    }
    if (!adminData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Valid email is required');
      return false;
    }
    if (adminData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (adminData.password !== adminData.confirm_password) {
      setError('Passwords do not match');
      return false;
    }
    setError('');
    return true;
  };

  const handleNext = async () => {
    if (activeStep === 0 && !validateInstitutionStep()) return;
    if (activeStep === 1 && !validateAdminStep()) return;

    if (activeStep === steps.length - 1) {
      await handleRegistration();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleRegistration = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Create Firebase auth user for admin
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        adminData.email,
        adminData.password
      );

      const adminFirebaseId = userCredential.user.uid;

      // Create institution in Firestore
      const institutionId = await createInstitution({
        ...institutionData,
        admin_id: adminFirebaseId,
        principal_id: adminFirebaseId,
        admin_email: adminData.email,
        admin_name: `${adminData.first_name} ${adminData.last_name}`,
        plan: 'basic',
        max_users: 500,
        max_storage_gb: 50,
        subscription_status: 'trial',
        trial_days_remaining: 14
      });

      // CRITICAL: Create authoritative role document in top-level users collection
      // This is required for completeAuthenticationFlow to work when user logs back in (STEP 4 FIX)
      try {
        await createUserRoleDocument(
          adminFirebaseId,
          adminData.email,
          'admin',
          institutionId,
          adminFirebaseId,
          { name: `${adminData.first_name} ${adminData.last_name}` }
        );
        console.log('✅ Created role document for admin in users collection');
      } catch (roleErr) {
        console.warn('⚠️ Warning: Could not create role document:', roleErr.message);
      }

      // CREATE ADMIN USER DOCUMENT IN INSTITUTION'S USERS SUBCOLLECTION
      const adminUserRef = doc(db, 'institutions', institutionId, 'users', adminFirebaseId);
      await setDoc(adminUserRef, {
        id: adminFirebaseId,
        email: adminData.email,
        name: `${adminData.first_name} ${adminData.last_name}`,
        role: 'admin',
        phone: institutionData.phone,
        status: 'active',
        createdAt: new Date(),
        permissions: {
          manage_users: true,
          manage_branches: true,
          manage_classes: true,
          manage_subjects: true,
          manage_students: true,
          view_analytics: true,
          manage_billing: true
        }
      });

      // Store user info in session
      const adminUser = {
        id: adminFirebaseId,
        email: adminData.email,
        name: `${adminData.first_name} ${adminData.last_name}`,
        role: 'admin',
        institution_id: institutionId,
        institution_name: institutionData.name
      };

      sessionStorage.setItem('logged_user', JSON.stringify(adminUser));
      setUserInfo(adminUser);

      setSuccess('Institution registered successfully! Redirecting to dashboard...');
      
      setTimeout(() => {
        navigate('/admin-dashboard');
      }, 2000);
    } catch (err) {
      console.error('Registration error:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      
      // Detailed error messages
      if (err.code === 'auth/email-already-in-use') {
        setError('Email is already registered');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Use at least 6 characters.');
      } else if (err.code === 'permission-denied') {
        setError('Permission denied. Check Firestore security rules are properly configured.');
      } else if (err.code === 'failed-precondition') {
        setError('Firestore database not initialized. Please check Firebase setup.');
      } else if (err.message?.includes('Cannot read properties')) {
        setError('Firebase configuration error. Ensure Firestore is properly initialized in firebaseconf.jsx');
      } else {
        setError(err.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Tell us about your institution
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Institution Name"
                  name="name"
                  value={institutionData.name}
                  onChange={handleInstitutionChange}
                  placeholder="e.g., ABC College of Engineering"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Institution Type"
                  name="type"
                  value={institutionData.type}
                  onChange={handleInstitutionChange}
                >
                  <MenuItem value="college">College</MenuItem>
                  <MenuItem value="school">School</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={institutionData.email}
                  onChange={handleInstitutionChange}
                  placeholder="admin@institution.com"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={institutionData.phone}
                  onChange={handleInstitutionChange}
                  placeholder="+91 XXXXX XXXXX"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Year Established"
                  name="established_year"
                  type="text"
                  value={institutionData.established_year}
                  onChange={handleInstitutionChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={institutionData.address}
                  onChange={handleInstitutionChange}
                  placeholder="Street address"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={institutionData.city}
                  onChange={handleInstitutionChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={institutionData.state}
                  onChange={handleInstitutionChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Zip Code"
                  name="zipCode"
                  value={institutionData.zipCode}
                  onChange={handleInstitutionChange}
                />
              </Grid>
              {institutionData.type === 'school' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Board"
                    name="board"
                    value={institutionData.board}
                    onChange={handleInstitutionChange}
                  >
                    <MenuItem value="CBSE">CBSE</MenuItem>
                    <MenuItem value="ICSE">ICSE</MenuItem>
                    <MenuItem value="State">State Board</MenuItem>
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={institutionData.description}
                  onChange={handleInstitutionChange}
                  placeholder="Brief description of your institution"
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Create your admin account
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={adminData.first_name}
                  onChange={handleAdminChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={adminData.last_name}
                  onChange={handleAdminChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={adminData.email}
                  onChange={handleAdminChange}
                  placeholder="your.email@example.com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={adminData.password}
                  onChange={handleAdminChange}
                  placeholder="At least 6 characters"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirm_password"
                  type="password"
                  value={adminData.confirm_password}
                  onChange={handleAdminChange}
                  placeholder="Re-enter password"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Review your information
            </Typography>
            <Paper elevation={0} sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
                INSTITUTION DETAILS
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#999' }}>Name</Typography>
                  <Typography variant="body1">{institutionData.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#999' }}>Type</Typography>
                  <Typography variant="body1">{institutionData.type.toUpperCase()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#999' }}>Email</Typography>
                  <Typography variant="body1">{institutionData.email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#999' }}>Phone</Typography>
                  <Typography variant="body1">{institutionData.phone}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ color: '#999' }}>Address</Typography>
                  <Typography variant="body1">
                    {institutionData.address}, {institutionData.city}, {institutionData.state} {institutionData.zipCode}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
                ADMIN ACCOUNT
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#999' }}>Name</Typography>
                  <Typography variant="body1">
                    {adminData.first_name} {adminData.last_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: '#999' }}>Email</Typography>
                  <Typography variant="body1">{adminData.email}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Register Your Institution
        </Typography>
        <Typography variant="body1" sx={{ color: '#666' }}>
          Join our SaaS platform to manage your college or school efficiently
        </Typography>
      </Box>

      <Card>
        <CardContent sx={{ p: 4 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {renderStepContent()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0 || loading}
            >
              Back
            </Button>
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : activeStep === steps.length - 1 ? (
                'Register Institution'
              ) : (
                'Next'
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
